import React, { useEffect, useRef } from 'react';

import { AccountFieldDefinition } from '@flipdish/api-client-typescript';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type FieldProps, Field, useFormikContext } from 'formik';
import get from 'lodash/get';
import { Translate } from 'react-localize-redux';

import { useAppSelector } from '../../../../../custom-hooks/useAppSelector';
import { formikValidate } from '../../../../../helpers/validation';
import * as dynamicHelpers from '../bankAccFieldTextHelpers/DynamicLabel';
import FormItemLayout from '../FormItemLayout';
import BankAccountFormBussinesTypeFields from './BankAccountFormBussinesTypeFields';
import FieldWrapper from './FieldWrapper';
import { useBankAddressLookup } from './useBankAddressLookup';

export interface IPropsDynamicFields {
  countryFields: AccountFieldDefinition[] | undefined;
}
export type Props = IPropsDynamicFields;

interface PopulatedAccountField {
  Key: string | undefined;
  DisplayName: string | undefined;
  Value: string;
}

interface BankFormValues {
  PopulatedAccountFields: PopulatedAccountField[];
  BusinessType: string;
  AccountName: string;
  BankName: string;
  BankAddress: string;
}

const validate = (value: string, fieldName: string) => {
  return (
    formikValidate.required(value) || formikValidate.max_string_custom(100)(value.length, fieldName)
  );
};

const validateDynamic = (
  value: string,
  fieldName: string | undefined,
  maxLength: number | undefined,
  minLength: number | undefined
) => {
  return (
    formikValidate.required(value) ||
    (minLength &&
      fieldName &&
      formikValidate.min_string_custom(minLength)(value.length, fieldName)) ||
    (maxLength && fieldName && formikValidate.max_string_custom(maxLength)(value.length, fieldName))
  );
};

const DynamicFormFields = (props: Props) => {
  const { countryFields = [] } = props;
  const editView = window.location.pathname.endsWith('/edit');
  const { values, setFieldValue } = useFormikContext<BankFormValues>();

  const [disabledFieldsBeforeAccountValidation, setDisabledFieldsBeforeAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
    });

  const [loadingFieldsDuringAccountValidation, setLoadingFieldsDuringAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
    });

  // Reference to store the debounce timer so we can clear it on blur
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize PopulatedAccountFields based on country field definitions
  // Track the country fields to reinitialize when country changes
  const previousCountryFieldsRef = useRef<string>('');

  useEffect(() => {
    if (countryFields.length > 0) {
      const currentFieldsKey = countryFields.map((f) => f.Key).join(',');

      const fieldsChanged = currentFieldsKey !== previousCountryFieldsRef.current;
      const arrayEmpty = (values.PopulatedAccountFields?.length ?? 0) === 0;

      if (fieldsChanged || arrayEmpty) {
        const existingFields = values.PopulatedAccountFields || [];
        const initialFields = countryFields.map((fieldDef) => {
          const existingField = existingFields.find(
            (f: PopulatedAccountField) => f.Key === fieldDef.Key
          );
          return {
            Key: fieldDef.Key,
            DisplayName: fieldDef.DisplayName,
            Value: existingField?.Value || '',
          };
        });

        setFieldValue('PopulatedAccountFields', initialFields);

        previousCountryFieldsRef.current = currentFieldsKey;
      }
    }
  }, [countryFields, values.PopulatedAccountFields.length, setFieldValue]);

  // Use the custom hook and get the triggerValidation function
  const { triggerValidation, lookupStatus } = useBankAddressLookup(
    setDisabledFieldsBeforeAccountValidation,
    setLoadingFieldsDuringAccountValidation,
    debounceTimerRef
  );

  const renderDynamicContent = () => {
    const dynamicFields = countryFields.map(function (dynamicField, index) {
      return (
        <Grid item xs={12} key={index}>
          <FieldWrapper>
            <Field
              name={`PopulatedAccountFields.${index}.Value`}
              validate={(value: string) =>
                validateDynamic(
                  value,
                  dynamicField.DisplayName,
                  dynamicField.MaxLength,
                  dynamicField.MinLength
                )
              }
            >
              {({ field, form }: FieldProps) => {
                const { errors, isSubmitting, touched } = form;
                const fieldError = get(errors, field.name) as string | undefined;
                const showError = editView
                  ? !!fieldError
                  : !!fieldError && (get(touched, field.name) as boolean | undefined);

                const isSwiftField = dynamicField.DisplayName === 'Swift';
                const fieldDisabled = isSwiftField
                  ? isSubmitting || disabledFieldsBeforeAccountValidation.swift
                  : isSubmitting;
                const showSpinner = isSwiftField && loadingFieldsDuringAccountValidation.swift;

                // Check if this field should trigger validation on blur
                const shouldValidateOnBlur =
                  dynamicField.DisplayName === 'IBAN' ||
                  dynamicField.DisplayName === 'Sort Code' ||
                  dynamicField.DisplayName === 'Account Number';

                const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                  // Call Formik's default onBlur
                  field.onBlur(e);

                  // If this is IBAN, SortCode, or AccountNumber, trigger immediate validation
                  if (shouldValidateOnBlur) {
                    // Clear any pending debounce timer
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                      debounceTimerRef.current = null;
                    }
                    // Trigger immediate validation using the hook's exposed function
                    triggerValidation();
                  }
                };

                return (
                  <TextField
                    variant="standard"
                    {...field}
                    onChange={(e) =>
                      form.setFieldValue(`PopulatedAccountFields.${index}`, {
                        Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                        DisplayName: dynamicField.DisplayName,
                        Value: e.target.value.replace(/\s/g, ''),
                      })
                    }
                    onBlur={handleBlur}
                    //@ts-ignore
                    label={<Translate id={`${dynamicHelpers.getDynamicLabel(dynamicField.Key)}`} />}
                    autoComplete="off"
                    fullWidth
                    disabled={fieldDisabled}
                    error={showError}
                    inputProps={{
                      'data-fd': `bank_dynamic_fields_${dynamicHelpers.getDynamicLabel(
                        dynamicField.Key
                      )}`,
                      ...(isSwiftField
                        ? {
                            'aria-busy': loadingFieldsDuringAccountValidation.swift,
                          }
                        : {}),
                    }}
                    InputProps={
                      showSpinner
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText={showError ? <>{fieldError}</> : undefined}
                    margin="none"
                  />
                );
              }}
            </Field>
          </FieldWrapper>
        </Grid>
      );
    });
    return dynamicFields;
  };

  return (
    <FormItemLayout label={<Translate id="Bank_account" />}>
      {/* Persistent lookup lifecycle for a11y + E2E; not visual UI. */}
      <div
        data-fd="bank-account-lookup"
        aria-busy={lookupStatus === 'loading'}
        data-status={lookupStatus}
        hidden
      />
      {!editView && <BankAccountFormBussinesTypeFields />}
      {editView && (
        <FieldWrapper>
          <Field name={'BusinessType'}>
            {({ field }: FieldProps) => {
              return (
                <TextField
                  variant="standard"
                  {...field}
                  label={<Translate id={'Account_type'} />}
                  fullWidth
                  inputProps={{
                    'data-fd': 'Account_type',
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="none"
                />
              );
            }}
          </Field>
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Field name={'AccountName'} validate={(value: string) => validate(value, 'Account Name')}>
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const showError = editView
              ? !!fieldError
              : !!fieldError && (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Account_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_account_name',
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
      {renderDynamicContent()}

      <FieldWrapper>
        <Field name={'BankName'} validate={(value: string) => validate(value, 'Bank Name')}>
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = get(errors, field.name) as string | undefined;
            const showError = editView
              ? !!fieldError
              : !!fieldError && (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Bank_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankName}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_name',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankName,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankName
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? <>{fieldError}</> : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>

      <FieldWrapper>
        <Field name={'BankAddress'} validate={(value: string) => validate(value, 'Address')}>
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const showError = editView
              ? !!fieldError
              : !!fieldError && (touched[field.name] as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id="Bank_Address" />}
                autoComplete="off"
                fullWidth
                multiline
                maxRows={3}
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankAddress}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_address',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankAddress,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankAddress
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
    </FormItemLayout>
  );
};

export default DynamicFormFields;
